/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Tab, Tabs } from "@nordcloud/gnui";
import { useTabsWithUrl } from "~/hooks";
import { EstateTabsData } from "../components";
import { getResourceVisabilityDates } from "../utils";
import {
  GCPServicesTabContents,
  GCPServicesList,
  PodsListTabContents,
  MetaTagsTabContents,
  RelatedResourcesTabContents,
  AdvancedDataTabContents,
  DetailsTabContents,
  BenefitedEstateRecords,
  SkuCostsList,
  externalIntegrationTabs,
} from "./components";
import {
  useGetAdvancedData,
  useDetailsMetadata,
  useLabelsAndResourcesTabMetadata,
  useTabVisibility,
  useGCPServices,
} from "./hooks";

type Props = {
  data: EstateTabsData;
  isReservedInstance?: boolean;
};

export function EstateDetailsTabs({ data, isReservedInstance = false }: Props) {
  const { activeTab, setActiveTab } = useTabsWithUrl();

  const { caption: advancedDataTabCaption, json } = useGetAdvancedData({
    data,
  });

  const resourceType = data.estateRecord?.providerType ?? "";

  const { caption: metaTagsTabCaption, heading: metaTagsTabHeading } =
    useLabelsAndResourcesTabMetadata({
      resourceType,
    });

  const { caption: detailsTabCaption, heading: detailsTabHeading } =
    useDetailsMetadata({
      resourceType,
    });

  const { accountName, projectResourceName } = useGCPServices(data);

  const {
    showAdvancedDataTab,
    showEnrichments,
    showEstateRecords,
    showGCPServiceRelatedResources,
    showMetaDetails,
    showPods,
    showServices,
    showMetaTags,
    showRelatedResources,
    showSkus,
  } = useTabVisibility(data, isReservedInstance);

  return (
    <Tabs step={activeTab} handleTab={setActiveTab}>
      {showGCPServiceRelatedResources && (
        <Tab
          heading="Related Resources"
          caption="Search result for related resources"
        >
          <GCPServicesTabContents
            details={data.details}
            metaTags={data.metaTags}
            cloudAccountId={accountName}
          />
        </Tab>
      )}
      {showPods && (
        <Tab data-testid="pods-list" heading="Pods" caption="List of Pods">
          <PodsListTabContents
            details={data.details}
            provider={data.estateRecord?.provider}
          />
        </Tab>
      )}
      {showServices && (
        <Tab heading="Services" caption="Project services">
          <GCPServicesList
            projectResourceName={projectResourceName}
            cloudAccountId={accountName}
          />
        </Tab>
      )}
      {showEstateRecords && (
        <Tab
          heading="Estate Records"
          caption="Estate records benefited from Reservations and Commitments"
        >
          <BenefitedEstateRecords />
        </Tab>
      )}
      <Tab heading={detailsTabHeading} caption={detailsTabCaption}>
        <DetailsTabContents
          data={data.metadata}
          showMetaDetails={showMetaDetails}
          resourceType={resourceType}
          resourceLifecycleDates={getResourceVisabilityDates(data.estateRecord)}
        />
      </Tab>
      {showMetaTags && (
        <Tab heading={metaTagsTabHeading} caption={metaTagsTabCaption}>
          <MetaTagsTabContents metaTags={data.metaTags} />
        </Tab>
      )}
      {showRelatedResources && (
        <Tab
          heading="Related Resources"
          caption="Search result for related resources"
        >
          <RelatedResourcesTabContents details={data.details} />
        </Tab>
      )}
      {showAdvancedDataTab && (
        <Tab heading="Advanced Data" caption={advancedDataTabCaption}>
          <AdvancedDataTabContents metadata={data.metadata} jsonData={json} />
        </Tab>
      )}
      {showEnrichments && externalIntegrationTabs(data.enrichments)}
      {showSkus && (
        <Tab heading="SKUs" caption="SKUs costs">
          <SkuCostsList
            provider={data.estateRecord?.provider}
            nid={data.estateRecord?.id}
          />
        </Tab>
      )}
    </Tabs>
  );
}
